import * as XLSX from 'xlsx';

export interface ImportedModelRow {
  displayName: string;
  slug: string;
  upstreamModel: string;
  endpoint: string;
  apiKey: string;
  contextTokens: number;
  inputPerMillion: number;
  outputPerMillion: number;
  cachedInputPerMillion: number;
  cacheReadPerMillion: number;
  image1k: number;
  image2k: number;
  image4k: number;
}

function parseNumber(value: string | undefined): number {
  const parsed = Number(String(value ?? '').replace(/[$¥,]/g, '').trim());
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

function parseContext(value: string | undefined): number {
  const match = /^([\d.]+)\s*([KM])?$/i.exec(String(value ?? '').trim());
  if (!match) return 128000;
  const multiplier = match[2]?.toUpperCase() === 'M' ? 1_000_000 : match[2]?.toUpperCase() === 'K' ? 1_000 : 1;
  return Math.max(1, Math.round(Number(match[1]) * multiplier));
}

export function parseModelWorkbook(bytes: Uint8Array): ImportedModelRow[] {
  const workbook = XLSX.read(bytes, { type: 'array', cellText: true, cellDates: false });
  const sheetName = workbook.SheetNames.includes('ForUsers') ? 'ForUsers' : workbook.SheetNames[0];
  if (!sheetName) throw new Error('工作簿中没有可导入的工作表');
  const worksheet = workbook.Sheets[sheetName];
  if (!worksheet) throw new Error('找不到 ForUsers 工作表');
  const rows = XLSX.utils.sheet_to_json<string[]>(worksheet, { header: 1, defval: '' }).filter((row) => row.some((value) => String(value).trim()));
  const headers = rows.shift()?.map((value) => value.trim().toLowerCase()) ?? [];
  const index = (name: string) => headers.findIndex((header) => header === name.toLowerCase());
  const value = (row: string[], name: string) => row[index(name)] ?? '';
  return rows.map((row) => ({
    displayName: value(row, '显式名称'), slug: value(row, '实际请求模型').trim(), upstreamModel: value(row, 'Upstream Model').trim(),
    endpoint: value(row, '模型请求端点').trim(), apiKey: value(row, '请求密钥').trim(), contextTokens: parseContext(value(row, '上下文长度')),
    inputPerMillion: parseNumber(value(row, 'input/¥')), outputPerMillion: parseNumber(value(row, 'output/¥')),
    cachedInputPerMillion: parseNumber(value(row, 'cache input/¥')), cacheReadPerMillion: parseNumber(value(row, 'cache read/¥')),
    image1k: parseNumber(value(row, '1kimage/¥')), image2k: parseNumber(value(row, '2kimage/¥')), image4k: parseNumber(value(row, '4kimage/¥')),
  })).filter((row) => row.slug && row.upstreamModel);
}

export async function readModelWorkbook(file: File): Promise<ImportedModelRow[]> {
  return parseModelWorkbook(new Uint8Array(await file.arrayBuffer()));
}
